import {MonthlyOfficeVisitReport} from "../../api/VisitWithStatStore";
import React, {useMemo} from "react";
import {VisitStatBlock} from "../../components/visit-stat-block/VisitStatBlock";
import {Button, IconButton, List, ListItem, ListItemButton, ListItemText, Stack, Typography} from "@mui/material";

import {downloadGhostUsagesCsv} from "./DownloadGhostUsagesCsv";
import {KeyVaultChip} from "../../components/key-vault-chip/KeyVaultChip";
import DownloadIcon from "@mui/icons-material/Download";
import {TooltipKeyLink} from "./TooltipKeyLink";
import {combineCmp, numberCmp, stringCmp} from "../../util/SortUtil";
import UpdateIcon from "@mui/icons-material/Update";
import {useKeyHistoryDialog} from "../history-dialog/HistoryDialogContext";

export function GhostKeysStatBlock({report}: {
    report: MonthlyOfficeVisitReport,
}) {
    const validation = report.validation;
    const openKeyHistoryDialog = useKeyHistoryDialog();

    const orderedKeys = useMemo(() => {
            return validation.ghostUsages
                .map(ghostUsage => ({
                    keyNumber: ghostUsage.keyNumber,
                    keyData: ghostUsage.keyData,
                    keyVault: [...(ghostUsage.keyData?.history || [])].sort(combineCmp(
                        stringCmp(it => it.to ?? '9999-12-01', true),
                        stringCmp(it => it.from ?? '', true),
                    ))[0]?.vault,
                    'not-issued': ghostUsage.usages.filter(usage => usage.type === 'not-issued').length,
                    'no-history': ghostUsage.usages.filter(usage => usage.type === 'no-history').length,
                    'ghost': ghostUsage.usages.filter(usage => usage.type === 'ghost').length,
                }))
                .sort(combineCmp(
                    numberCmp('not-issued', true),
                    numberCmp('no-history', true),
                    numberCmp('ghost', true),
                ));
        },
        [validation]
    );

    return (
        <>
            <VisitStatBlock
                color={orderedKeys.length > 0 ? orderedKeys[0]['not-issued'] > 0 ? 'error' : 'warning' : 'inherit'}
                tooltip={
                    <List>
                        <ListItem sx={{display: 'flex', gap: '16px'}}>
                            <ListItemText
                                primary="Ghost Usages"
                                secondary="Keys according to report used on days when they were in vault, lost, broken, or deactivated"
                                slotProps={{
                                    secondary: {color: 'grey.400'}
                                }}
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemButton
                                sx={{mx: -2}}
                                component={Button}
                                onClick={() => downloadGhostUsagesCsv(report)}
                                startIcon={<DownloadIcon/>}
                            >Download CSV</ListItemButton>
                        </ListItem>
                        {orderedKeys.map(ghostUsage => (
                            <ListItem key={ghostUsage.keyNumber}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    justifyContent="space-between"
                                    width="100%"
                                >
                                    <Stack direction="row" gap={1}>
                                        <TooltipKeyLink keyNumber={ghostUsage.keyNumber}/>
                                        {ghostUsage.keyVault && <KeyVaultChip keyVault={ghostUsage.keyVault} size="small"/>}
                                    </Stack>
                                    <Stack direction="row" gap={1}>
                                        {ghostUsage.keyData && (
                                            <IconButton
                                                size="small"
                                                onClick={event => ghostUsage.keyData && openKeyHistoryDialog(
                                                    ghostUsage.keyData,
                                                    {x: event.clientX, y: event.clientY},
                                                )}
                                                aria-label={`Key history for key ${ghostUsage.keyNumber}`}
                                                sx={{color: 'white', p: 0}}
                                            >
                                                <UpdateIcon fontSize="small"/>
                                            </IconButton>
                                        )}
                                        {ghostUsage['not-issued'] > 0 && (
                                            <Typography variant="body2">
                                                Not issued: {ghostUsage['not-issued']}
                                            </Typography>
                                        )}
                                        {ghostUsage['no-history'] > 0 && (
                                            <Typography variant="body2">
                                                No history: {ghostUsage['no-history']}
                                            </Typography>
                                        )}
                                        {ghostUsage['ghost'] > 0 && (
                                            <Typography variant="body2">
                                                Ghost: {ghostUsage['ghost']}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            </ListItem>
                        ))}
                    </List>
                }
                tooltipWidth={400}
            >
                Ghost usages: {validation.ghostUsages.length}
            </VisitStatBlock>
        </>
    );
}
